import React, { useState } from "react";
import { ChartDataResponse } from "../ceo-dashboard.service";
import CustomTable from "src/components/custom-table/custom-table";
import { ColumnsType } from "antd/es/table";
import { Dayjs } from "dayjs";
import dayjs from "dayjs";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";

dayjs.extend(isSameOrBefore);

interface InflowsOutflowsTableProps {
  chartData: ChartDataResponse | null;
  loading: boolean;
  dateRange: [Dayjs | null, Dayjs | null];
}

const InflowsOutflowsTable: React.FC<InflowsOutflowsTableProps> = ({
  chartData,
  loading,
  dateRange,
}) => {
  const handleTableChange = () => {
    // Table change handler - not used for this table but required by CustomTable
  };

  // Generate all months in the selected range
  const generateMonthsRange = () => {
    const [startDate, endDate] = dateRange;
    if (!startDate || !endDate) return [];

    const months: string[] = [];
    let current = startDate.clone();

    while (current.isSameOrBefore(endDate, 'month')) {
      months.push(current.format('MMM YYYY'));
      current = current.add(1, 'month');
    }

    return months;
  };

  // Process data for the table
  const processTableData = () => {
    if (!chartData?.data) return [];

    const months = generateMonthsRange();
    const groupedData: { [key: string]: { [key: string]: number } } = {};

    // Initialize months with zero values
    months.forEach(month => {
      if (!groupedData[month]) {
        groupedData[month] = {
          'Inflow': 0,
          'Outflow': 0,
          'Net Flow': 0
        };
      }
    });

    // Process chart data
    chartData.data.forEach(item => {
      const apiMonth = item.month_name; // Format: "August 2025"
      const amount = parseFloat(item.amount_kwd);

      // Convert API month format to chart format for matching
      const chartMonth = dayjs(apiMonth, 'MMMM YYYY').format('MMM YYYY'); // "Aug 2025"

      if (months.includes(chartMonth)) {
        if (item.trx_type === 'Deposit' || item.trx_type === 'Subscription') {
          groupedData[chartMonth]['Inflow'] += amount;
        } else if (item.trx_type === 'Withdrawal' || item.trx_type === 'Redemption') {
          groupedData[chartMonth]['Outflow'] += amount;
        }
      }
    });

    // Calculate Net Flow for each month
    months.forEach(month => {
      groupedData[month]['Net Flow'] = groupedData[month]['Inflow'] - groupedData[month]['Outflow'];
    });

    // Transform data for table display
    const flows = ['Inflow', 'Outflow', 'Net Flow'];
    return flows.map(flow => {
      const rowData: any = { flow };
      let total = 0;

      months.forEach(month => {
        const value = groupedData[month][flow];
        rowData[month] = value;
        total += value;
      });

      rowData['Total'] = total;
      return rowData;
    });
  };

  // Generate dynamic columns
  const generateColumns = (): ColumnsType<any> => {
    const months = generateMonthsRange();

    const totalColumns = months.length + 1; // +1 for Total column
    const monthColumnWidth = Math.floor(75 / months.length); // 75% divided by number of months
    const totalColumnWidth = 75 - (monthColumnWidth * months.length); // Remaining percentage

    const columns: ColumnsType<any> = [
      {
        title: "Flows",
        dataIndex: "flow",
        key: "flow",
        width: "15%",
        render: (flow: string) => (
          <div className="text-zinc-800 text-xs font-semibold font-['Montserrat']">
            {flow}
          </div>
        ),
      },
    ];

    // Add month columns
    months.forEach(month => {
      columns.push({
        title: month,
        dataIndex: month,
        key: month,
        width: `${monthColumnWidth}%`,
        render: (amount: number, record) => {
          const isNetFlow = record.flow === 'Net Flow';
          const colorClass = isNetFlow
            ? (amount >= 0 ? 'text-green-600' : 'text-red-600')
            : 'text-zinc-800';

          return (
            <div className={`text-xs font-medium font-['Montserrat'] ${colorClass}`}>
              {amount.toLocaleString()} KWD
            </div>
          );
        },
      });
    });

    // Add Total column
    columns.push({
      title: "Total",
      dataIndex: "Total",
      key: "Total",
      width: "10%",
      render: (total: number, record) => {
        const isNetFlow = record.flow === 'Net Flow';
        const colorClass = isNetFlow
          ? (total >= 0 ? 'text-green-600' : 'text-red-600')
          : 'text-zinc-800';

        return (
          <div className={`text-xs font-bold font-['Montserrat'] ${colorClass} bg-slate-50 px-2 py-1 rounded`}>
            {total.toLocaleString()} KWD
          </div>
        );
      },
    });

    return columns;
  };

  const tableData = processTableData();
  const columns = generateColumns();

  return (
    <div className="w-full flex flex-col justify-start items-start shadow-md bg-white rounded-lg gap-5 py-5">
      <div className="w-full inline-flex justify-start items-center gap-3.5 px-5">
        <div className="flex-1 justify-start text-zinc-800 text-sm font-semibold font-['Montserrat']">
          Monthly Inflows and Outflows
        </div>
      </div>

      <div className="w-full px-5 bg-white [&_.ant-table]:font-['Montserrat'] [&_.ant-table-thead>tr>th]:bg-slate-200 [&_.ant-table-thead>tr>th]:border-b [&_.ant-table-thead>tr>th]:border-gray-300 [&_.ant-table-thead>tr>th]:text-sky-900 [&_.ant-table-thead>tr>th]:text-xs [&_.ant-table-thead>tr>th]:font-semibold [&_.ant-table-thead>tr>th]:font-['Montserrat'] [&_.ant-table-thead>tr>th]:px-5 [&_.ant-table-thead>tr>th]:py-3 [&_.ant-table-tbody>tr>td]:border-b [&_.ant-table-tbody>tr>td]:border-zinc-300 [&_.ant-table-tbody>tr>td]:px-5 [&_.ant-table-tbody>tr>td]:py-4 [&_.ant-table-tbody>tr>td]:bg-white [&_.ant-table-tbody>tr:hover>td]:bg-slate-50 [&_.ant-pagination]:mx-5 [&_.ant-pagination]:my-4 [&_.ant-pagination]:font-['Montserrat'] [&_.ant-table-container]:border-none [&_.ant-table-content]:overflow-x-auto [&_.ant-empty-description]:font-['Montserrat'] [&_.ant-empty-description]:text-zinc-500">
        <CustomTable
          data={tableData}
          loading={loading}
          showPagination={false}
          handleTableChange={handleTableChange}
          columns={columns}
          style={{ width: '100%' }}
        />
      </div>
    </div>
  );
};

export default InflowsOutflowsTable;